import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Banknote, BookOpen, TrendingUp, RotateCcw, Coins, BarChart3, Trash2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency, formatCurrency } from '../contexts/CompanyContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useT } from '../contexts/LanguageContext';

const ALLOWED = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'ACCOUNTANT'];

const TABS = [
  { key: 'accounts',  tKey: 'tab.accounts', label: 'Bank & Cash',        icon: Banknote },
  { key: 'coa',       tKey: 'tab.coa',      label: 'Chart of Accounts',  icon: BookOpen },
  { key: 'advances',  tKey: 'tab.advances', label: 'Advance Payments',   icon: Coins },
  { key: 'returns',   tKey: 'tab.returns',  label: 'Purchase Returns',   icon: RotateCcw },
  { key: 'pnl',       tKey: 'tab.pnl',      label: 'P&L Statement',      icon: BarChart3 },
];

export default function AdvancedAccounting() {
  const { user } = useContext(AuthContext);
  const { symbol } = useCurrency();
  const t = useT();
  const [tab, setTab] = useState('accounts');

  if (!ALLOWED.includes(user?.role)) {
    return <Layout><div className="p-6 text-center text-muted-foreground">Forbidden — accounting access required.</div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="adv-accounting-page" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-amber-600" />{t('nav.advAccounting')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('advAccounting.subtitle') === 'advAccounting.subtitle' ? 'Bank/Cash accounts, Chart of Accounts, Advance Payments, Purchase Returns, and Profit & Loss.' : t('advAccounting.subtitle')}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {TABS.map(tb => {
            const Icon = tb.icon;
            const active = tab === tb.key;
            return (
              <button
                key={tb.key}
                type="button"
                data-testid={`adv-tab-${tb.key}`}
                onClick={() => setTab(tb.key)}
                className={`px-3 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                  active ? 'bg-amber-500 text-slate-900 shadow' : 'bg-white border border-slate-200 text-slate-700 hover:border-amber-300'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />{(() => { const tx = t(tb.tKey); return tx === tb.tKey ? tb.label : tx; })()}
              </button>
            );
          })}
        </div>

        {tab === 'accounts' && <BankCashTab symbol={symbol} />}
        {tab === 'coa'      && <ChartTab />}
        {tab === 'advances' && <AdvancesTab symbol={symbol} />}
        {tab === 'returns'  && <ReturnsTab symbol={symbol} />}
        {tab === 'pnl'      && <PnLTab symbol={symbol} />}
      </div>
    </Layout>
  );
}

// ====== Bank/Cash Accounts ======
function BankCashTab({ symbol }) {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ kind: 'bank', name: '', bank_name: '', account_number: '', iban: '', currency: 'AED', opening_balance: 0 });
  const fetchRows = () => axios.get(`${API}/financial-accounts`, { withCredentials: true }).then(r => setRows(r.data || [])).catch(() => {});
  useEffect(() => { fetchRows(); }, []);
  const create = async () => {
    if (!draft.name) return toast.error('Name is required');
    try {
      await axios.post(`${API}/financial-accounts`, { ...draft, opening_balance: Number(draft.opening_balance || 0) }, { withCredentials: true });
      toast.success('Account added'); setShowNew(false); fetchRows();
      setDraft({ kind: 'bank', name: '', bank_name: '', account_number: '', iban: '', currency: 'AED', opening_balance: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete account?')) return;
    try { await axios.delete(`${API}/financial-accounts/${id}`, { withCredentials: true }); fetchRows(); }
    catch { toast.error('Delete failed'); }
  };
  return (
    <Card className="p-5">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-foreground">Bank &amp; Cash Accounts</h2>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button data-testid="new-account-btn" size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-3.5 h-3.5 mr-1" />Add account</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Account</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label>Kind</Label>
                <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v })}>
                  <SelectTrigger data-testid="account-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="bank">Bank</SelectItem><SelectItem value="cash">Cash</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Name *</Label><Input data-testid="account-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder={draft.kind === 'bank' ? 'e.g. Emirates NBD - Operating' : 'e.g. Petty Cash - Office'} /></div>
              {draft.kind === 'bank' && (
                <>
                  <div><Label>Bank</Label><Input value={draft.bank_name} onChange={(e) => setDraft({ ...draft, bank_name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Account #</Label><Input value={draft.account_number} onChange={(e) => setDraft({ ...draft, account_number: e.target.value })} /></div>
                    <div><Label>IBAN</Label><Input value={draft.iban} onChange={(e) => setDraft({ ...draft, iban: e.target.value })} /></div>
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Currency</Label><Input value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} /></div>
                <div><Label>Opening Balance</Label><Input type="number" value={draft.opening_balance} onChange={(e) => setDraft({ ...draft, opening_balance: e.target.value })} /></div>
              </div>
              <Button data-testid="submit-account-btn" onClick={create} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map(r => (
          <Card key={r.id} data-testid={`account-card-${r.id}`} className={`p-4 border-l-4 ${r.kind === 'bank' ? 'border-l-sky-500' : 'border-l-emerald-500'}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">{r.kind}</div>
                <div className="font-semibold text-foreground">{r.name}</div>
                {r.bank_name && <div className="text-xs text-slate-600">{r.bank_name}</div>}
                {r.iban && <div className="text-xs text-muted-foreground font-mono">{r.iban}</div>}
              </div>
              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-end">
              <div className="text-xs text-muted-foreground">Balance ({r.currency})</div>
              <div className="text-lg font-bold text-foreground">{formatCurrency(r.current_balance, r.currency)}</div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground col-span-full text-center py-6">No accounts yet.</p>}
      </div>
    </Card>
  );
}

// ====== Chart of Accounts ======
function ChartTab() {
  const [rows, setRows] = useState([]);
  const fetchRows = () => axios.get(`${API}/chart-of-accounts`, { withCredentials: true }).then(r => setRows(r.data || [])).catch(() => {});
  useEffect(() => { fetchRows(); }, []);
  const seed = async () => {
    try { await axios.post(`${API}/chart-of-accounts/seed`, {}, { withCredentials: true }); toast.success('Seeded default chart'); fetchRows(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
  };
  return (
    <Card className="p-5">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-foreground">Chart of Accounts</h2>
        {rows.length === 0 && <Button data-testid="seed-coa-btn" size="sm" onClick={seed} className="bg-amber-500 hover:bg-amber-600 text-slate-900">Seed defaults</Button>}
      </div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No chart of accounts yet. Click "Seed defaults" to load a 22-line default plan.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wide text-slate-500"><th className="px-4 py-2">Code</th><th className="px-4 py-2">Name</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Parent</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} data-testid={`coa-row-${r.code}`} className="border-b last:border-0">
                  <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
                  <td className="px-4 py-2 font-medium">{r.name}</td>
                  <td className="px-4 py-2 capitalize">{r.type}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{r.parent_code || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ====== Advance Payments ======
function AdvancesTab({ symbol }) {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ direction: 'out', party_name: '', amount: 0, currency: 'AED', notes: '' });
  const fetchRows = () => axios.get(`${API}/advance-payments`, { withCredentials: true }).then(r => setRows(r.data || [])).catch(() => {});
  useEffect(() => { fetchRows(); }, []);
  const create = async () => {
    if (!draft.party_name) return toast.error('Party name is required');
    try {
      await axios.post(`${API}/advance-payments`, { ...draft, amount: Number(draft.amount || 0) }, { withCredentials: true });
      toast.success('Advance recorded'); setShowNew(false); fetchRows();
      setDraft({ direction: 'out', party_name: '', amount: 0, currency: 'AED', notes: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <Card className="p-5">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-foreground">Advance Payments</h2>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild><Button data-testid="new-advance-btn" size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-3.5 h-3.5 mr-1" />New Advance</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Record Advance Payment</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label>Direction</Label>
                <Select value={draft.direction} onValueChange={(v) => setDraft({ ...draft, direction: v })}>
                  <SelectTrigger data-testid="advance-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="out">Advance to supplier (paid out)</SelectItem>
                    <SelectItem value="in">Advance from customer (received)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Party Name *</Label><Input data-testid="advance-party-input" value={draft.party_name} onChange={(e) => setDraft({ ...draft, party_name: e.target.value })} placeholder="Supplier or customer name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount</Label><Input type="number" data-testid="advance-amount-input" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} /></div>
                <div><Label>Currency</Label><Input value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} /></div>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>
              <Button data-testid="submit-advance-btn" onClick={create} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wide text-slate-500"><th className="px-4 py-2">Date</th><th className="px-4 py-2">Direction</th><th className="px-4 py-2">Party</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2 text-right">Applied</th><th className="px-4 py-2">Status</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`advance-row-${r.id}`} className="border-b last:border-0">
                <td className="px-4 py-2">{r.paid_at}</td>
                <td className="px-4 py-2 capitalize">{r.direction === 'out' ? 'Paid to supplier' : 'From customer'}</td>
                <td className="px-4 py-2 font-medium">{r.party_name}</td>
                <td className="px-4 py-2 text-right">{formatCurrency(r.amount, r.currency || symbol)}</td>
                <td className="px-4 py-2 text-right">{formatCurrency(r.applied_amount || 0, r.currency || symbol)}</td>
                <td className="px-4 py-2 capitalize">{r.status?.replace('_', ' ')}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="text-center py-6 text-slate-500">No advances yet</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ====== Purchase Returns ======
function ReturnsTab({ symbol }) {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ supplier_id: '', supplier_name: '', refund_amount: 0, refund_method: 'credit_note', notes: '' });
  const fetchRows = () => axios.get(`${API}/purchase-returns`, { withCredentials: true }).then(r => setRows(r.data || [])).catch(() => {});
  useEffect(() => {
    fetchRows();
    axios.get(`${API}/suppliers`, { withCredentials: true }).then(r => setSuppliers(r.data || [])).catch(() => {});
  }, []);
  const create = async () => {
    if (!draft.supplier_id || !draft.refund_amount) return toast.error('Supplier and refund amount required');
    try {
      const supp = suppliers.find(s => s.id === draft.supplier_id);
      await axios.post(`${API}/purchase-returns`, { ...draft, supplier_name: supp?.name || draft.supplier_name, refund_amount: Number(draft.refund_amount) }, { withCredentials: true });
      toast.success('Return recorded'); setShowNew(false); fetchRows();
      setDraft({ supplier_id: '', supplier_name: '', refund_amount: 0, refund_method: 'credit_note', notes: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <Card className="p-5">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-foreground">Purchase Returns</h2>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild><Button data-testid="new-return-btn" size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-3.5 h-3.5 mr-1" />New Return</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Record Purchase Return</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label>Supplier *</Label>
                <Select value={draft.supplier_id} onValueChange={(v) => setDraft({ ...draft, supplier_id: v })}>
                  <SelectTrigger data-testid="return-supplier"><SelectValue placeholder="Pick supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Refund Amount *</Label><Input type="number" data-testid="return-amount-input" value={draft.refund_amount} onChange={(e) => setDraft({ ...draft, refund_amount: e.target.value })} /></div>
              <div><Label>Refund Method</Label>
                <Select value={draft.refund_method} onValueChange={(v) => setDraft({ ...draft, refund_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit_note">Credit Note</SelectItem>
                    <SelectItem value="refund_to_bank">Refund to Bank</SelectItem>
                    <SelectItem value="refund_to_cash">Refund to Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>
              <Button data-testid="submit-return-btn" onClick={create} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wide text-slate-500"><th className="px-4 py-2">Date</th><th className="px-4 py-2">Supplier</th><th className="px-4 py-2">Method</th><th className="px-4 py-2 text-right">Refund</th><th className="px-4 py-2">Status</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`return-row-${r.id}`} className="border-b last:border-0">
                <td className="px-4 py-2">{r.return_date}</td>
                <td className="px-4 py-2 font-medium">{r.supplier_name}</td>
                <td className="px-4 py-2 capitalize">{r.refund_method?.replace(/_/g, ' ')}</td>
                <td className="px-4 py-2 text-right text-emerald-700 font-medium">{formatCurrency(r.refund_amount, symbol)}</td>
                <td className="px-4 py-2 capitalize">{r.status}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="5" className="text-center py-6 text-slate-500">No returns recorded</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ====== P&L (gated) ======
function PnLTab({ symbol }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    setLocked(false);
    axios.get(`${API}/accounting/pnl?year=${year}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => {
        if (e.response?.status === 403) {
          setLocked(true);
          setData(null);
        } else {
          toast.error('Failed to load P&L');
        }
      });
  }, [year]);

  if (locked) {
    return (
      <Card className="p-10 text-center">
        <Lock className="w-12 h-12 mx-auto text-amber-500 mb-3" />
        <h3 className="font-semibold text-foreground">P&amp;L is included with the Pro+ Advanced Analytics feature</h3>
        <p className="text-sm text-muted-foreground mt-1">Upgrade your plan to unlock month-over-month profit &amp; loss reporting.</p>
        <Button asChild className="mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900"><a href="/subscription">Upgrade plan</a></Button>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-foreground">Profit &amp; Loss — {year}</h2>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger data-testid="pnl-year-select" className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>{[0, 1, 2, 3].map(off => { const y = new Date().getFullYear() - off; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}</SelectContent>
        </Select>
      </div>

      {!data ? <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3"><div className="text-xs uppercase text-slate-500">Income</div><div className="text-lg font-bold text-emerald-700">{formatCurrency(data.totals.income, symbol)}</div></Card>
            <Card className="p-3"><div className="text-xs uppercase text-slate-500">Expense</div><div className="text-lg font-bold text-rose-700">{formatCurrency(data.totals.expense, symbol)}</div></Card>
            <Card className="p-3"><div className="text-xs uppercase text-slate-500">Payroll</div><div className="text-lg font-bold text-amber-700">{formatCurrency(data.totals.payroll, symbol)}</div></Card>
            <Card className="p-3"><div className="text-xs uppercase text-slate-500">Net</div><div className={`text-lg font-bold ${data.totals.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatCurrency(data.totals.net, symbol)}</div></Card>
          </div>

          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tickFormatter={(m) => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m - 1]} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="income" name="Income" fill="#10b981" />
              <Bar dataKey="expense" name="Expense" fill="#ef4444" />
              <Bar dataKey="payroll" name="Payroll" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
